import React, { useState, useEffect, useRef } from "react";
import apiClient from "../apiClient";
import { useShop } from "../context/ShopContext";
import { useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import EmptyState from "../components/EmptyState";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";

/* ─── helpers ────────────────────────────────────────────────── */
const DASHBOARD_API = "/Liquor/GetDashboardSummary";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening";
};

const fmtINR = (v) => {
  if (v == null || isNaN(v)) return "₹0";
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
};

const fmtNum = (v) => (v == null ? "0" : Math.round(v).toLocaleString("en-IN"));

const DONUT_COLORS = ["#F59E0B", "#7B1D4F"];

/* ─── count-up animation hook ────────────────────────────────── */
function useCountUp(target, duration = 1100) {
  const [val, setVal] = useState(0);
  const rafRef  = useRef(null);
  const prevRef = useRef(0);

  useEffect(() => {
    const to = typeof target === "number" && !isNaN(target) ? target : 0;
    const from = prevRef.current;
    if (from === to) { setVal(to); return; }
    const startTs = performance.now();
    const animate = (now) => {
      const p = Math.min((now - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (p < 1) { rafRef.current = requestAnimationFrame(animate); }
      else        { setVal(to); prevRef.current = to; }
    };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]); // eslint-disable-line

  return val;
}

/* ─── SectionLabel ────────────────────────────────────────────── */
const SectionLabel = ({ children }) => (
  <div style={{
    fontWeight: 700, fontSize: 11, color: "#4B5563",
    textTransform: "uppercase", letterSpacing: 1.4, marginBottom: 12,
    display: "flex", alignItems: "center", gap: 8,
  }}>
    <div style={{ width: 3, height: 14, background: "#7B1D4F", borderRadius: 2, flexShrink: 0 }} />
    <span>{children}</span>
    <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, #E5E7EB 0%, transparent 100%)" }} />
  </div>
);

/* ─── SummaryKpiCard ─────────────────────────────────────────── */
const SummaryKpiCard = ({ label, rawValue, icon, accent, sub }) => {
  const animated = useCountUp(rawValue ?? 0);
  return (
    <div style={{
      flex: "1 1 150px", background: "#fff", borderRadius: 12,
      padding: "15px 17px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
      borderTop: `3px solid ${accent}`,
      display: "flex", flexDirection: "column", gap: 8,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8, background: accent + "18",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, flexShrink: 0,
        }}>{icon}</div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1 }}>{label}</div>
          {sub && <div style={{ fontSize: 9, color: "#D1D5DB", marginTop: 2 }}>{sub}</div>}
        </div>
      </div>
      <div style={{ fontSize: 21, fontWeight: 800, color: "#111827", letterSpacing: -0.5 }}>{fmtINR(animated)}</div>
    </div>
  );
};

/* ─── TopCard (legacy, kept for compat) ─────────────────────── */
const TopCard = ({ label, rawValue, icon, accent }) => {
  const animated = useCountUp(rawValue ?? 0);
  return (
    <div style={{
      flex: "1 1 160px", background: "#fff", borderRadius: 14,
      padding: "18px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9, background: accent + "1a",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
        }}>{icon}</div>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</span>
      </div>
      <div style={{ fontSize: 23, fontWeight: 800, color: "#111827", letterSpacing: -0.5 }}>{fmtINR(animated)}</div>
    </div>
  );
};

/* ─── PerfTile ───────────────────────────────────────────────── */
const PerfTile = ({ label, rawValue, icon, accent, onClick }) => {
  const animated = useCountUp(rawValue ?? 0);
  return (
    <div
      className="perf-tile"
      onClick={onClick}
      style={{
        flex: "0 0 auto", minWidth: 140, background: "#fff", borderRadius: 10,
        padding: "12px 14px", boxShadow: "0 1px 5px rgba(0,0,0,0.06)",
        borderLeft: `3px solid ${accent}`,
        cursor: "pointer", transition: "box-shadow 0.15s, transform 0.1s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 4px 14px ${accent}30`; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 5px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "none"; }}
    >
      <div style={{ fontSize: 10, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
        {icon}&nbsp;{label}
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>{fmtINR(animated)}</div>
      <div style={{ fontSize: 9, color: accent, marginTop: 4, fontWeight: 600 }}>Tap for details →</div>
    </div>
  );
};

/* ─── PerfModal ──────────────────────────────────────────────── */
const PerfModal = ({ tile, todayData, monthlyData, onClose }) => {
  if (!tile) return null;
  const T = todayData;
  const M = monthlyData;
  const relatedRows = {
    "देशी Sales":         [{ label: "Monthly Desi Sale",        val: M?.desi         }, { label: "Today Desi Commission", val: T?.desiCommission }],
    "English Sales":      [{ label: "Monthly English Sale",     val: M?.english      }, { label: "Today Eng Commission",  val: T?.commission     }],
    "Expenses":           [{ label: "Monthly Expense",          val: M?.exp          }, { label: "Today Salary",          val: T?.salary         }],
    "Desi Commission":    [{ label: "Today Desi Sale",          val: T?.desi         }, { label: "Monthly Desi Sale",     val: M?.desi           }],
    "English Commission": [{ label: "Today English Sale",       val: T?.english      }, { label: "Monthly English Sale",  val: M?.english        }],
    "Credit":             [{ label: "Today Debit",              val: T?.debit        }, { label: "Net Cash",              val: T?.amount         }],
    "Debit":              [{ label: "Today Credit",             val: T?.credit       }, { label: "Net Cash",              val: T?.amount         }],
    "Salary":             [{ label: "Monthly Salary",           val: M?.salary       }, { label: "Today Expenses",        val: T?.exp            }],
    "PhonePe":            [{ label: "Today Total Sales",        val: T?.sales        }, { label: "Net Cash",              val: T?.amount         }],
  };
  const rows = relatedRows[tile.label] || [];
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ background: "#fff", borderRadius: 18, padding: "28px 24px", maxWidth: 360, width: "100%", boxShadow: "0 24px 64px rgba(0,0,0,0.22)", position: "relative" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 14, background: "#F3F4F6", border: "none", borderRadius: 8, padding: "5px 9px", cursor: "pointer", fontSize: 13, color: "#6B7280" }}>✕</button>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: tile.accent + "1a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{tile.icon}</div>
          <div>
            <div style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Today's {tile.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#111827", letterSpacing: -0.5 }}>{fmtINR(tile.rawValue)}</div>
          </div>
        </div>
        {rows.length > 0 && (
          <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>Related Figures</div>
            {rows.map((r, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "#6B7280" }}>{r.label}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{fmtINR(r.val || 0)}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 20, padding: "10px 14px", background: tile.accent + "0d", borderRadius: 10, fontSize: 12, color: tile.accent, fontWeight: 600 }}>
          Data reflects today's transactions only.
        </div>
      </div>
    </div>
  );
};

/* ─── nav config ─────────────────────────────────────────────── */
const NAV_GROUPS = [
  {
    perm: "खर्च", title: "खर्च", subtitle: "EXPENSES & FINANCE", icon: "💸", iconBg: "#FFF7ED", iconColor: "#EA580C",
    items: [
      { perm: "देसी कमीशन",       label: "देशी कमीशन",        sub: "Desi Commission",    icon: "₹",  bg: "#F97316", path: "/commissiondesi" },
      { perm: "अंग्रेजी कमीशन",    label: "अंग्रेजी कमीशन",    sub: "English Commission", icon: "₹",  bg: "#F97316", path: "/commission" },
      { perm: "स्टाफ वेतन",       label: "स्टाफ वेतन",       sub: "Staff Salary",       icon: "👤", bg: "#F97316", path: "/employee" },
      { perm: "नामे / जमा",       label: "नामे / जमा",       sub: "Debit / Credit",     icon: "💳", bg: "#F97316", path: "/creditdebit" },
      { perm: "प्रोडक्ट दर अंतर", label: "प्रोडक्ट दर अंतर", sub: "Product Rate Diff",  icon: "📉", bg: "#F97316", path: "/productratedifference" },
      { perm: "फोनपे जमा",        label: "फोनपे जमा",        sub: "PhonePe Deposit",    icon: "📱", bg: "#F97316", path: "/phonepaycredit" },
    ],
    panel: "left",
  },
  {
    perm: "रिपोर्ट", title: "रिपोर्ट", subtitle: "REPORTS", icon: "📊", iconBg: "#FDF4F8", iconColor: "#5E1540",
    items: [
      { perm: "बिकी रिपोर्ट",  label: "बिकी रिपोर्ट",  sub: "Sales Report",   icon: "📊", bg: "#7B1D4F", path: "/salesreport" },
      { perm: "पर्चा रिपोर्ट", label: "पर्चा रिपोर्ट", sub: "Invoice Report", icon: "📑", bg: "#7B1D4F", path: "/report" },
      { perm: "खर्च रिपोर्ट",  label: "खर्च रिपोर्ट",  sub: "Expense Report", icon: "📑", bg: "#7B1D4F", path: "/expensereport" },
      { perm: "प्रिंट पर्चा",  label: "प्रिंट पर्चा",  sub: "Print Invoice",  icon: "🖨️", bg: "#7B1D4F", path: "/printslip" },
    ],
    panel: "right",
  },
];

/* ─── main component ─────────────────────────────────────────── */
export default function Home() {
  const { shopId, companyId, userName, shopName, groupId } = useShop();
  const navigate = useNavigate();

  const pad2 = n => String(n).padStart(2, "0");
  const todayISO = (() => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`; })();

  const [dashDate,       setDashDate]       = useState(todayISO);
  const [todayData,      setTodayData]      = useState(null);
  const [monthlyData,    setMonthlyData]    = useState(null);
  const [allTimeTotal,   setAllTimeTotal]   = useState(0);
  const [chartRows,      setChartRows]      = useState([]);
  const [weeklyChartData,  setWeeklyChartData]  = useState([]);
  const [monthlyChartData, setMonthlyChartData] = useState([]);
  const [chartPeriod,    setChartPeriod]    = useState("Week");
  const [donutSource,    setDonutSource]    = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [activityMain, setActivityMain] = useState("aamad");
  const [activitySub,  setActivitySub]  = useState("desi");
  const [perms,          setPerms]          = useState([]);
  const [permsLoaded,    setPermsLoaded]    = useState(false);
  const [totalPerms,     setTotalPerms]     = useState(0);

  const [activeTile,        setActiveTile]        = useState(null);
  const [weather,           setWeather]           = useState(null);
  const [timelineData,      setTimelineData]      = useState([]);
  const [stockData,         setStockData]         = useState([]);
  const [dailyTxnRec,       setDailyTxnRec]       = useState([]);
  const [dailyTxnTrn,       setDailyTxnTrn]       = useState([]);
  const [lowStockData,      setLowStockData]      = useState([]);
  const [topSellingData,    setTopSellingData]    = useState([]);
  const [timelineLoading,   setTimelineLoading]   = useState(false);
  const [lowStockLoading,   setLowStockLoading]   = useState(false);
  const [topSellingLoading, setTopSellingLoading] = useState(false);

  useEffect(() => {
    if (!shopId) return;
    setLoading(true);
    apiClient.post(DASHBOARD_API, { shopId, companyId, date: new Date(dashDate).toISOString() })
      .then((res) => {
        const d = res.data?.data;
        if (!d) return;
        setTodayData(d.today);
        setMonthlyData(d.monthly);
        setAllTimeTotal(d.allTimeTotal || 0);
        setWeeklyChartData(d.weeklyChart || []);
        setMonthlyChartData(d.monthlyChart || []);
        setChartRows(d.weeklyChart || []);
        setDonutSource(d.donutSource);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [shopId, companyId, dashDate]); // eslint-disable-line

  useEffect(() => {
    if (!shopId) return;
    if (!groupId) { setPermsLoaded(true); return; }
    apiClient.post("/Liquor/GetGroupPermissions", { groupId, shopId })
      .then((res) => {
        const data = res.data?.data || [];
        setTotalPerms(data.length);
        setPerms(data.filter(m => m.canView).map(m => m.menuName.trim()));
        setPermsLoaded(true);
      }).catch(() => setPermsLoaded(true));
  }, [groupId, shopId]); // eslint-disable-line

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => {
        fetch(`https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true`)
          .then(r => r.json())
          .then(d => {
            const wc = d?.current_weather?.weathercode ?? -1;
            const temp = Math.round(d?.current_weather?.temperature ?? 0);
            const icon = wc === 0 ? "☀️" : wc <= 3 ? "🌤️" : wc <= 48 ? "🌫️" : wc <= 55 ? "🌦️" : wc <= 65 ? "🌧️" : wc <= 75 ? "🌨️" : wc <= 82 ? "🌩️" : wc <= 99 ? "⛈️" : "🌡️";
            setWeather({ temp, icon });
          })
          .catch(() => {});
      },
      () => {}
    );
  }, []);

  const fetchDailyTxn = React.useCallback(() => {
    if (!shopId) return;
    setTimelineLoading(true);
    const today = dashDate;
    apiClient.post("/Liquor/GetDailyTransaction", { shopId, stockDate: today })
      .then((txnRes) => {
        const rec = txnRes.data?.data?.trnReceived  || [];
        const trn = txnRes.data?.data?.trnTransfer  || [];
        const led = txnRes.data?.data?.tblProductStockLedger || [];
        setDailyTxnRec(rec);
        setDailyTxnTrn(trn);
        const tl = [
          ...rec.map(r => ({ time: r.rectimeStamp, icon: "⬇️", color: "#10B981", text: `${r.receivedHead || r.typeName || "Stock"} Received`, sub: r.productName || "" })),
          ...trn.map(r => ({ time: r.rectimeStamp, icon: "⬆️", color: "#F97316", text: `${r.transferHead || r.typeName || "Stock"} Transferred`, sub: r.productName || "" })),
        ];
        const parchaMap = {};
        led.forEach(l => {
          const k = (l.stockDate||"").slice(0,10);
          if (k && !parchaMap[k]) parchaMap[k] = { time: null, icon: "📋", color: "#7B1D4F", text: "Parcha Submitted", sub: "" };
        });
        Object.values(parchaMap).forEach(e => tl.push(e));
        tl.sort((a, b) => {
          if (!a.time && !b.time) return 0;
          if (!a.time) return 1;
          if (!b.time) return -1;
          return new Date(b.time) - new Date(a.time);
        });
        setTimelineData(tl.slice(0, 12));
      }).catch(console.error).finally(() => setTimelineLoading(false));
  }, [shopId, dashDate]); // eslint-disable-line

  useEffect(() => { fetchDailyTxn(); }, [fetchDailyTxn]);

  useEffect(() => {
    const onVisibleTxn = () => { if (document.visibilityState === "visible") fetchDailyTxn(); };
    document.addEventListener("visibilitychange", onVisibleTxn);
    return () => document.removeEventListener("visibilitychange", onVisibleTxn);
  }, [fetchDailyTxn]);

  useEffect(() => {
    if (!shopId) return;
    setTopSellingLoading(true);
    const today = dashDate;
    Promise.all([
      apiClient.post("/Liquor/GetTodaySalesProduct", { shopId, typeId: "1", stockDate: today }),
      apiClient.post("/Liquor/GetTodaySalesProduct", { shopId, typeId: "2", stockDate: today }),
    ]).then(([eRes, dRes]) => {
      const all = [
        ...(eRes.data?.data || []).map(p => ({ name: p.pName, packing: p.packing, mrp: parseFloat(p.mrp)||0, qty: Math.round(parseFloat(p.salesToday)||0), catType: "English" })),
        ...(dRes.data?.data || []).map(p => ({ name: p.pName, packing: p.packing, mrp: parseFloat(p.mrp)||0, qty: Math.round(parseFloat(p.salesToday)||0), catType: "Desi" })),
      ].filter(p => p.qty > 0).sort((a, b) => b.qty - a.qty).slice(0, 5);
      setTopSellingData(all);
    }).catch(console.error).finally(() => setTopSellingLoading(false));
  }, [shopId, dashDate]); // eslint-disable-line

  const fetchStockData = React.useCallback(() => {
    if (!shopId) return;
    setLowStockLoading(true);
    const today = dashDate;
    Promise.all([
      apiClient.post("/Liquor/GetStockProducts", { shopId, typeId: "1", stockDate: today }),
      apiClient.post("/Liquor/GetStockProducts", { shopId, typeId: "2", stockDate: today }),
    ]).then(([eRes, dRes]) => {
      const enriched = [
        ...(eRes.data?.data || []).map(p => ({ ...p, catType: "English" })),
        ...(dRes.data?.data || []).map(p => ({ ...p, catType: "Desi"    })),
      ].map(p => ({
        id:          p.id || p.productId,
        name:        p.pName,
        packing:     p.packing,
        cat:         p.categoryName || p.catName || p.category || p.catType,
        catType:     p.catType,
        mrp:         parseFloat(p.mrp || p.mRP || p.MRP) || 0,
        receivedQty: parseFloat(p.receivedQty) || 0,
        transferQty: parseFloat(p.transferQty) || 0,
        stock:       Math.max(0, (parseFloat(p.openingStock)||0) + (parseFloat(p.receivedQty)||0) - (parseFloat(p.transferQty)||0)),
      }));
      setStockData(enriched);
      setLowStockData([...enriched].filter(p => p.stock <= 5).sort((a, b) => a.stock - b.stock));
    }).catch(console.error).finally(() => setLowStockLoading(false));
  }, [shopId, dashDate]); // eslint-disable-line

  useEffect(() => { fetchStockData(); }, [fetchStockData]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") fetchStockData(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchStockData]);

  const handlePeriod = (p) => {
    setChartPeriod(p);
    setChartRows(p === "Week" ? weeklyChartData : monthlyChartData);
  };

  const canSeeDashboard  = !permsLoaded || totalPerms === 0 || perms.includes("Dashboard");
  const canSeeSection = (sectionPerm) => {
    if (!permsLoaded)     return false;
    if (totalPerms === 0) return true;
    return perms.includes(sectionPerm);
  };
  const canSeeItem = (_sectionPerm, itemPerm) => {
    if (!permsLoaded)     return false;
    if (totalPerms === 0) return true;
    return perms.includes(itemPerm);
  };

  const donutData = donutSource
    ? [{ name: "Desi", value: donutSource.desi }, { name: "English", value: donutSource.english }]
    : [];

  const nowStr = new Date().toLocaleString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });

  const T = todayData;
  const M = monthlyData;

  const { pidToCat, nameToCat } = React.useMemo(() => {
    const byId = {}, byName = {};
    stockData.forEach(p => {
      const info = { catType: p.catType, mrp: p.mrp, name: p.name };
      if (p.id) byId[String(p.id)] = info;
      if (p.name) byName[p.name.trim().toLowerCase()] = info;
    });
    return { pidToCat: byId, nameToCat: byName };
  }, [stockData]);

  const activityChartData = (() => {
    const txns = activityMain === "aamad" ? dailyTxnRec : dailyTxnTrn;
    const catFilter = activitySub === "desi" ? "Desi" : "English";
    const byProduct = {};
    txns.forEach(r => {
      const pid   = String(r.productId || r.ProductId || "");
      const pName = (r.productName || r.ProductName || r.pName || "").trim();
      const info  = (pid && pidToCat[pid]) || (pName && nameToCat[pName.toLowerCase()]) || {};
      if (info.catType && info.catType !== catFilter) return;
      const key = pid || pName;
      if (!key) return;
      if (!byProduct[key]) {
        byProduct[key] = { name: pName || info.name || "Unknown", mrp: info.mrp || r.mrp || r.MRP || r.rate || 0, catType: info.catType || catFilter, qty: 0 };
      }
      byProduct[key].qty += (parseFloat(r.qty) || 0);
    });
    return Object.values(byProduct).filter(p => p.qty > 0).sort((a, b) => b.qty - a.qty);
  })();

  // Aamad/Bhejan summary totals
  const totalReceivedQty   = dailyTxnRec.reduce((s, r) => s + (parseFloat(r.qty) || 0), 0);
  const totalTransferredQty = dailyTxnTrn.reduce((s, r) => s + (parseFloat(r.qty) || 0), 0);
  const netMovement = totalReceivedQty - totalTransferredQty;

  const PERF_TILES = [
    { perm: "देशी बिक्री",          label: "देशी Sales",         rawValue: T?.desi           || 0, icon: "🍶", accent: "#F59E0B" },
    { perm: "अंग्रेजी बिक्री",      label: "English Sales",      rawValue: T?.english        || 0, icon: "🍾", accent: "#7B1D4F" },
    { perm: "देसी कमीशन राशि",      label: "Desi Commission",    rawValue: T?.desiCommission || 0, icon: "🍶", accent: "#F59E0B" },
    { perm: "अंग्रेजी कमीशन राशि",  label: "English Commission", rawValue: T?.commission     || 0, icon: "💼", accent: "#7B1D4F" },
    { perm: "डेबिट",                label: "Debit",              rawValue: T?.debit          || 0, icon: "⬇️", accent: "#EC4899" },
  ];
  const visiblePerfTiles = PERF_TILES.filter(c => canSeeItem(null, c.perm));

  // 6 summary KPI cards
  const SUMMARY_CARDS = [
    { perm: "आज की बिक्री",  label: "Today Sale",   rawValue: T?.sales    || 0, icon: "📈", accent: "#7B1D4F", sub: "Today's revenue" },
    { perm: "मासिक बिक्री",  label: "Monthly Sale", rawValue: M?.sales    || 0, icon: "📅", accent: "#5E1540", sub: "This month" },
    { perm: "नेट कैश",       label: "Net Cash",     rawValue: T?.amount   || 0, icon: "💵", accent: "#10B981", sub: "Cash in hand" },
    { perm: "क्रेडिट",       label: "Credit",       rawValue: T?.credit   || 0, icon: "⬆️", accent: "#7B1D4F", sub: "Total credit" },
    { perm: "आज का खर्च",    label: "Expenses",     rawValue: T?.exp      || 0, icon: "💸", accent: "#EF4444", sub: "Today's spend" },
    { perm: "फोनपे राजस्व",  label: "PhonePe",      rawValue: T?.phonePay || 0, icon: "📱", accent: "#5C2D91", sub: "Digital" },
  ];
  const visibleSummaryCards = SUMMARY_CARDS.filter(c => canSeeItem(null, c.perm));

  const outOfStockItems = React.useMemo(
    () => stockData.filter(p => p.stock === 0).sort((a, b) => a.name.localeCompare(b.name)),
    [stockData]
  );

  const QA_BUTTONS = [
    { group: "📋 पर्चा Entry",    check: () => canSeeItem("पर्चा", "पिछला देशी पर्चा"),      icon: "🍶", text: "देशी पर्चा",    sub: "Desi Sale",     mText: "Desi Sale",      mSub: "देशी पर्चा",      path: "/desistockentry",        bg: "rgba(255,255,255,0.13)", hbg: "rgba(255,255,255,0.24)", iBg: "rgba(255,255,255,0.18)" },
    { group: "📋 पर्चा Entry",    check: () => canSeeItem("पर्चा", "पिछला अंग्रेजी पर्चा"),  icon: "🍾", text: "English पर्चा", sub: "English Sale",  mText: "English Sale",   mSub: "English पर्चा",   path: "/stock",                 bg: "rgba(255,255,255,0.13)", hbg: "rgba(255,255,255,0.24)", iBg: "rgba(255,255,255,0.18)" },
    { group: "📦 Stock Movement",  check: () => canSeeItem("आमद भेजान", "देशी आमद"),          icon: "⬇️", text: "Desi Aamad",    sub: "Stock Inward",  mText: "Desi Aamad",     mSub: "Stock Inward",    path: "/editdesistockwithdate", bg: "rgba(16,185,129,0.18)",  hbg: "rgba(16,185,129,0.32)",  iBg: "rgba(16,185,129,0.28)"  },
    { group: "📦 Stock Movement",  check: () => canSeeItem("आमद भेजान", "देशी भेजान"),        icon: "⬆️", text: "Desi Bhejan",   sub: "Stock Outward", mText: "Desi Bhejan",    mSub: "Stock Outward",   path: "/editdesitransferstock", bg: "rgba(249,115,22,0.18)",  hbg: "rgba(249,115,22,0.32)",  iBg: "rgba(249,115,22,0.28)"  },
    { group: "📦 Stock Movement",  check: () => canSeeItem("आमद भेजान", "अंग्रेजी आमद"),      icon: "⬇️", text: "Angreji Aamad", sub: "Stock Inward",  mText: "English Aamad",  mSub: "Stock Inward",    path: "/editstockwithdate",     bg: "rgba(16,185,129,0.18)",  hbg: "rgba(16,185,129,0.32)",  iBg: "rgba(16,185,129,0.28)"  },
    { group: "📦 Stock Movement",  check: () => canSeeItem("आमद भेजान", "अंग्रेजी भेजान"),    icon: "⬆️", text: "Angreji Bhejan",sub: "Stock Outward", mText: "English Bhejan", mSub: "Stock Outward",   path: "/edittransferstock",     bg: "rgba(249,115,22,0.18)",  hbg: "rgba(249,115,22,0.32)",  iBg: "rgba(249,115,22,0.28)"  },
    { group: "⚡ Operations",      check: () => canSeeItem("खर्च", "पिछला खर्चा"),            icon: "💸", text: "Add Expense",   sub: "Daily Expense", mText: "Add Expense",    mSub: "Daily Expense",   path: "/expensedate",           bg: "rgba(239,68,68,0.18)",   hbg: "rgba(239,68,68,0.32)",   iBg: "rgba(239,68,68,0.25)"   },
    { group: "⚡ Operations",      check: () => perms.length === 0 || perms.includes("पर्चा"), icon: "🛒", text: "POS",           sub: "Point of Sale", mText: "POS",            mSub: "Point of Sale",   path: "/pos",                   bg: "rgba(92,45,145,0.18)",   hbg: "rgba(92,45,145,0.32)",   iBg: "rgba(92,45,145,0.25)"   },
  ];

  if (permsLoaded && totalPerms > 0 && !canSeeDashboard) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: 16, color: "#6B7280" }}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>Access Denied</div>
        <div style={{ fontSize: 14 }}>You don't have permission to view the Dashboard.</div>
      </div>
    );
  }

  return (
    <div className="home-page-wrapper" style={{ background: "#F5F6FA", minHeight: "100vh", padding: "20px 24px 52px" }}>

      {/* ══════════════════════════════════════════
          HEADER
      ══════════════════════════════════════════ */}
      <div className="hd-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 24 }}>🍷</span>
            <h4 style={{ margin: 0, fontWeight: 800, fontSize: 21, color: "#111827" }}>
              {greeting()}, <span style={{ color: "#7B1D4F" }}>{userName || "Staff"}</span>
            </h4>
          </div>
          <div style={{ color: "#6B7280", fontSize: 12, marginTop: 3, marginLeft: 34, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {nowStr}
            {weather && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#F3F4F6", borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 600, color: "#374151" }}>
                {weather.icon} {weather.temp}°C
              </span>
            )}
          </div>
        </div>
        <div className="hd-header-right" style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {/* Date picker */}
          <div style={{ display: "flex", alignItems: "center", gap: 5, background: dashDate !== todayISO ? "#FFF7ED" : "#F3F4F6", borderRadius: 10, padding: "4px 8px", border: dashDate !== todayISO ? "1.5px solid #FED7AA" : "1.5px solid #E5E7EB" }}>
            <span style={{ fontSize: 13 }}>📅</span>
            <input
              type="date"
              value={dashDate}
              max={todayISO}
              onChange={e => setDashDate(e.target.value)}
              style={{ border: "none", background: "transparent", fontSize: 12, fontWeight: 600, color: dashDate !== todayISO ? "#C2410C" : "#374151", outline: "none", cursor: "pointer" }}
            />
            {dashDate !== todayISO && (
              <button onClick={() => setDashDate(todayISO)} title="Back to today" style={{ background: "#F97316", border: "none", borderRadius: 6, color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 7px", cursor: "pointer" }}>Today</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 7, alignItems: "center", flexShrink: 0 }}>
            {dashDate === todayISO ? (
              <span style={{ background: "#D1FAE5", color: "#065F46", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20, display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#10B981", display: "inline-block" }} />
                Live
              </span>
            ) : (
              <span style={{ background: "#FFF7ED", color: "#C2410C", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20 }}>
                📜 History
              </span>
            )}
            <span style={{ background: "#FDF4F8", color: "#5E1540", fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20 }}>
              🏪 {shopName}
            </span>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          QUICK ACTIONS
      ══════════════════════════════════════════ */}
      {permsLoaded && (
        <div className="hd-qa" style={{
          background: "linear-gradient(135deg, #2D0A1C 0%, #4A1436 40%, #6D214F 100%)",
          borderRadius: 14, padding: "18px 20px", marginBottom: 20,
          boxShadow: "0 4px 20px rgba(61,13,38,0.28)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
            <span style={{ fontSize: 13 }}>⚡</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.50)", textTransform: "uppercase", letterSpacing: 1.5 }}>Quick Actions</span>
            <span style={{ marginLeft: "auto", fontSize: 9, color: "rgba(255,255,255,0.28)", fontWeight: 500 }}>Click to navigate instantly</span>
          </div>

          {/* ── Desktop: original group layout (hidden on mobile) ── */}
          {(() => {
            const visible = QA_BUTTONS.filter(b => b.check());
            const groups = [...new Set(visible.map(b => b.group))];
            return (
              <div className="qa-groups-desktop" style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                {groups.map(g => {
                  const btns = visible.filter(b => b.group === g);
                  return (
                    <div key={g} className="qa-group" style={{ flex: btns.length, minWidth: 0 }}>
                      <div className="qa-group-label" style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.38)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 7, paddingBottom: 5, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>{g}</div>
                      <div className="qa-btn-row" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {btns.map((btn, i) => (
                          <button key={i} onClick={() => navigate(btn.path)}
                            style={{
                              flex: "1 1 64px", background: btn.bg, border: "1.5px solid rgba(255,255,255,0.12)",
                              borderRadius: 9, padding: "7px 6px 6px",
                              display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                              cursor: "pointer", color: "#fff",
                              transition: "transform 0.12s, background 0.14s, box-shadow 0.12s",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = btn.hbg; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.22)"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = btn.bg; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
                          >
                            <div style={{ width: 26, height: 26, borderRadius: 8, background: btn.iBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>{btn.icon}</div>
                            <div style={{ fontSize: 10, fontWeight: 700, textAlign: "center", lineHeight: 1.2, letterSpacing: -0.2 }}>{btn.text}</div>
                            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.50)", textAlign: "center", lineHeight: 1.2 }}>{btn.sub}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* ── Mobile: compact 2-column grid (hidden on desktop) ── */}
          <div className="qa-grid-mobile" style={{ display: "none", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
            {QA_BUTTONS.filter(b => b.check()).map((btn, i) => (
              <button
                key={i}
                onClick={() => navigate(btn.path)}
                style={{
                  background: btn.bg, border: "1.5px solid rgba(255,255,255,0.11)",
                  borderRadius: 10, height: 72,
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "0 12px", cursor: "pointer", color: "#fff",
                  textAlign: "left", width: "100%", boxSizing: "border-box",
                  transition: "transform 0.12s, background 0.14s, box-shadow 0.12s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = btn.hbg; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.25)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = btn.bg; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: btn.iBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>{btn.icon}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.25, letterSpacing: -0.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{btn.mText}</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.50)", lineHeight: 1.3, marginTop: 3, whiteSpace: "nowrap" }}>{btn.mSub}</div>
                </div>
              </button>
            ))}
          </div>

        </div>
      )}

      {/* ══════════════════════════════════════════
          LOADING
      ══════════════════════════════════════════ */}
      {(loading || !permsLoaded) ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#9CA3AF" }}>
          <FaSpinner style={{ animation: "spin 1s linear infinite", fontSize: 24 }} />
          <div style={{ marginTop: 10, fontSize: 13 }}>Loading dashboard…</div>
        </div>
      ) : (
        <>
          {/* ══════════════════════════════════════════
              TODAY'S SUMMARY — 6 KPI cards
          ══════════════════════════════════════════ */}
          {visibleSummaryCards.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <SectionLabel>Today's Summary</SectionLabel>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {visibleSummaryCards.map(c => (
                  <SummaryKpiCard key={c.label} {...c} />
                ))}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════
              PERFORMANCE BREAKDOWN — horizontal strip
          ══════════════════════════════════════════ */}
          {visiblePerfTiles.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <SectionLabel>Performance Breakdown</SectionLabel>
              <div className="perf-breakdown-row" style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none", flexWrap: "nowrap" }}>
                {visiblePerfTiles.map(c => (
                  <PerfTile key={c.label} {...c} onClick={() => setActiveTile(c)} />
                ))}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════
              ANALYTICS ROW: Chart + Donut + Aamad/Bhejan
          ══════════════════════════════════════════ */}
          <div style={{ marginBottom: 20 }}>
            <SectionLabel>Sales Analytics</SectionLabel>
            <div className="charts-row" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>

              {/* Area Chart */}
              {canSeeSection("साप्ताहिक बिक्री") && (
                <div style={{
                  flex: "3 1 340px", background: "#fff", borderRadius: 14,
                  padding: "18px 18px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
                  height: 310, boxSizing: "border-box", overflow: "hidden",
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Sales Trend</div>
                      <div style={{ color: "#9CA3AF", fontSize: 11, marginTop: 2 }}>Revenue performance</div>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {["Week", "Month"].map((p) => (
                        <button key={p} onClick={() => handlePeriod(p)}
                          style={{ background: chartPeriod === p ? "#7B1D4F" : "#F3F4F6", color: chartPeriod === p ? "#fff" : "#6B7280", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={228}>
                    <AreaChart data={chartRows}>
                      <defs>
                        <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#7B1D4F" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#7B1D4F" stopOpacity={0}    />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => Math.round(v).toLocaleString("en-IN")} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }} formatter={(v) => [fmtINR(v), "Sales"]} />
                      <Area type="monotone" dataKey="sales" stroke="#7B1D4F" strokeWidth={2.5}
                        fill="url(#salesGrad)" dot={{ r: 3, fill: "#7B1D4F", strokeWidth: 0 }} activeDot={{ r: 5 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Revenue Donut */}
              {canSeeSection("रेवेन्यू विश्लेषण") && (
                <div style={{
                  flex: "1 1 180px", background: "#fff", borderRadius: 14,
                  padding: "18px 16px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
                  height: 310, boxSizing: "border-box", overflow: "hidden",
                }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Revenue Split</div>
                  <div style={{ color: "#9CA3AF", fontSize: 11, marginBottom: 6 }}>Desi vs English</div>
                  <ResponsiveContainer width="100%" height={148}>
                    <PieChart>
                      <Pie data={donutData} cx="50%" cy="50%" innerRadius={44} outerRadius={62}
                        paddingAngle={5} dataKey="value" startAngle={90} endAngle={-270}>
                        {donutData.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }} formatter={(v, n) => [fmtINR(v), n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                    {donutData.map((d, i) => {
                      const total = donutSource?.sales || 0;
                      const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
                      return (
                        <div key={d.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: DONUT_COLORS[i], display: "inline-block" }} />
                            <span style={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>{d.name}</span>
                          </div>
                          <div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>{fmtINR(d.value || 0)}</span>
                            <span style={{ fontSize: 10, color: "#9CA3AF", marginLeft: 3 }}>({pct}%)</span>
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 6, display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                      <span style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Today Total</span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#111827" }}>{fmtINR(donutSource?.sales || 0)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Aamad / Bhejan — compact */}
              {canSeeSection("आज की गतिविधि") && (
                <div style={{
                  flex: "2 1 240px", background: "#fff", borderRadius: 14,
                  padding: "14px 14px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
                  height: 310, boxSizing: "border-box", display: "flex", flexDirection: "column",
                }}>
                  {/* Header */}
                  <div style={{ flexShrink: 0, marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>आमद भेजान</div>
                    <div style={{ color: "#9CA3AF", fontSize: 11, marginTop: 1, marginBottom: 8 }}>Today's stock movement</div>

                    {/* Movement summary badges */}
                    <div style={{ display: "flex", gap: 5, marginBottom: 7 }}>
                      <div style={{ flex: 1, background: "#ECFDF5", borderRadius: 6, padding: "5px 7px", textAlign: "center" }}>
                        <div style={{ fontSize: 8, fontWeight: 700, color: "#065F46", textTransform: "uppercase", letterSpacing: 0.5 }}>⬇️ In</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: "#10B981", lineHeight: 1.2 }}>{fmtNum(totalReceivedQty)}</div>
                      </div>
                      <div style={{ flex: 1, background: "#FFF7ED", borderRadius: 6, padding: "5px 7px", textAlign: "center" }}>
                        <div style={{ fontSize: 8, fontWeight: 700, color: "#9A3412", textTransform: "uppercase", letterSpacing: 0.5 }}>⬆️ Out</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: "#F97316", lineHeight: 1.2 }}>{fmtNum(totalTransferredQty)}</div>
                      </div>
                      <div style={{ flex: 1, background: netMovement >= 0 ? "#F0FDF4" : "#FEF2F2", borderRadius: 6, padding: "5px 7px", textAlign: "center" }}>
                        <div style={{ fontSize: 8, fontWeight: 700, color: netMovement >= 0 ? "#065F46" : "#991B1B", textTransform: "uppercase", letterSpacing: 0.5 }}>Net</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: netMovement >= 0 ? "#10B981" : "#EF4444", lineHeight: 1.2 }}>{netMovement >= 0 ? "+" : ""}{fmtNum(netMovement)}</div>
                      </div>
                    </div>

                    {/* Toggles */}
                    <div style={{ display: "flex", gap: 5, marginBottom: 6 }}>
                      {[["aamad","⬇️ आमद","#10B981"],["bhejan","⬆️ भेजान","#F97316"]].map(([mode, lbl, clr]) => (
                        <button key={mode} onClick={() => { setActivityMain(mode); setActivitySub("desi"); }}
                          style={{ flex: 1, background: activityMain === mode ? clr : "#F3F4F6", color: activityMain === mode ? "#fff" : "#6B7280", border: "none", borderRadius: 6, padding: "5px 0", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 5 }}>
                      {[["desi","🍶 देशी"],["angreji","🍾 English"]].map(([sub, lbl]) => (
                        <button key={sub} onClick={() => setActivitySub(sub)}
                          style={{
                            flex: 1,
                            background: activitySub === sub ? (activityMain === "aamad" ? "#ECFDF5" : "#FFF7ED") : "#F9FAFB",
                            color: activitySub === sub ? (activityMain === "aamad" ? "#065F46" : "#9A3412") : "#9CA3AF",
                            border: `1.5px solid ${activitySub === sub ? (activityMain === "aamad" ? "#6EE7B7" : "#FED7AA") : "#F3F4F6"}`,
                            borderRadius: 6, padding: "4px 0", fontSize: 10, fontWeight: 700, cursor: "pointer",
                          }}>{lbl}</button>
                      ))}
                    </div>
                  </div>

                  {/* Column headers */}
                  <div style={{ flexShrink: 0, display: "grid", gridTemplateColumns: "1fr 50px 36px", gap: "0 6px", padding: "4px 2px 5px", borderBottom: "1.5px solid #F3F4F6", fontSize: 9, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    <span>Product</span><span style={{ textAlign: "right" }}>Rate</span><span style={{ textAlign: "right" }}>Qty</span>
                  </div>

                  {/* Product list */}
                  <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                    {lowStockLoading ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", paddingTop: 30, color: "#9CA3AF" }}>
                        <FaSpinner style={{ animation: "spin 1s linear infinite" }} />
                      </div>
                    ) : activityChartData.length === 0 ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", paddingTop: 24 }}>
                        <EmptyState message="No movement today" sub="Transactions will appear here" />
                      </div>
                    ) : activityChartData.map((p, i) => {
                      const maxQ = activityChartData[0]?.qty || 1;
                      const pct  = Math.round((p.qty / maxQ) * 100);
                      const barClr = activityMain === "aamad" ? "#10B981" : "#F97316";
                      return (
                        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 50px 36px", gap: "0 6px", alignItems: "center", padding: "5px 2px", borderBottom: "1px solid #F9FAFB" }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                            <div style={{ marginTop: 3, height: 3, background: "#F3F4F6", borderRadius: 2, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${pct}%`, background: barClr, borderRadius: 2 }} />
                            </div>
                          </div>
                          <div style={{ textAlign: "right", fontSize: 11, fontWeight: 700, color: "#374151" }}>₹{p.mrp}</div>
                          <div style={{ textAlign: "right", fontSize: 13, fontWeight: 800, color: barClr }}>{p.qty}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* ══════════════════════════════════════════
              PRODUCT ANALYTICS + RECENT ACTIVITY
          ══════════════════════════════════════════ */}
          <div style={{ marginBottom: 20 }}>
            <SectionLabel>Product Analytics &amp; Activity</SectionLabel>
            <div className="db-widget-row" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>

              {/* Top Selling */}
              <div className="db-widget" style={{ flex: "3 1 260px", background: "#fff", borderRadius: 14, padding: "18px 18px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)", maxHeight: 340, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexShrink: 0 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#FDF4F8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>🏆</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>Top Selling</div>
                    <div style={{ fontSize: 10, color: "#9CA3AF" }}>Today's bestsellers by qty</div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto auto", gap: "0 6px", fontWeight: 700, fontSize: 9, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5, paddingBottom: 5, borderBottom: "1.5px solid #F3F4F6", flexShrink: 0 }}>
                  <span>Product</span><span style={{ textAlign: "right" }}>Variant</span><span style={{ textAlign: "right" }}>Rate</span><span>Type</span><span style={{ textAlign: "right" }}>Qty</span>
                </div>
                {topSellingLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "#9CA3AF" }}><FaSpinner style={{ animation: "spin 1s linear infinite" }} /></div>
                ) : topSellingData.length === 0 ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "#9CA3AF", fontSize: 12 }}>No sales recorded today</div>
                ) : (
                  <div style={{ overflowY: "auto", flex: 1 }}>
                    {topSellingData.map((p, i) => {
                      const maxQ = topSellingData[0]?.qty || 1;
                      const pct  = Math.round((p.qty / maxQ) * 100);
                      return (
                        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto auto", gap: "0 6px", alignItems: "center", padding: "6px 4px", borderRadius: 6, marginBottom: 2, background: i === 0 ? "#FFFBEB" : "#F9FAFB" }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                            <div style={{ marginTop: 3, height: 3, background: "#E5E7EB", borderRadius: 2, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${pct}%`, background: "#7B1D4F", borderRadius: 2 }} />
                            </div>
                          </div>
                          <span style={{ fontSize: 10, color: "#6B7280", textAlign: "right", whiteSpace: "nowrap" }}>{p.packing}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textAlign: "right" }}>₹{p.mrp}</span>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 5px", borderRadius: 10, background: p.catType === "Desi" ? "#FDF4F8" : "#F0FDF4", color: p.catType === "Desi" ? "#7B1D4F" : "#065F46" }}>{p.catType}</span>
                          <span style={{ fontSize: 14, fontWeight: 800, color: "#7B1D4F", textAlign: "right", minWidth: 22 }}>{p.qty}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Low Stock */}
              <div className="db-widget" style={{ flex: "3 1 260px", background: "#fff", borderRadius: 14, padding: "18px 18px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)", maxHeight: 340, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexShrink: 0 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#FFF7ED", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>⚠️</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>Low Stock Alerts</div>
                    <div style={{ fontSize: 10, color: "#9CA3AF" }}>Products ≤ 5 units remaining</div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: "0 8px", fontWeight: 700, fontSize: 9, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5, paddingBottom: 5, borderBottom: "1.5px solid #F3F4F6", flexShrink: 0 }}>
                  <span>Product</span><span style={{ textAlign: "right" }}>Pack</span><span>Type</span><span style={{ textAlign: "right" }}>Stock</span>
                </div>
                {lowStockLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "#9CA3AF" }}><FaSpinner style={{ animation: "spin 1s linear infinite" }} /></div>
                ) : lowStockData.length === 0 ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "#9CA3AF", fontSize: 12 }}>All stocks are sufficient 🎉</div>
                ) : (
                  <div style={{ overflowY: "auto", flex: 1, maxHeight: window.innerWidth < 1364 ? 175 : undefined }}>
                    {lowStockData.map((p, i) => {
                      const level = p.stock === 0 ? "critical" : p.stock <= 3 ? "low" : "warn";
                      const clr   = level === "critical" ? "#EF4444" : level === "low" ? "#F97316" : "#F59E0B";
                      const bg    = level === "critical" ? "#FEF2F2" : level === "low" ? "#FFF7ED" : "#FFFBEB";
                      return (
                        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: "0 8px", alignItems: "center", padding: "6px 4px", borderRadius: 6, marginBottom: 2, background: bg }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                          <span style={{ fontSize: 10, color: "#6B7280", textAlign: "right" }}>{p.packing}</span>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 5px", borderRadius: 10, background: p.catType === "Desi" ? "#FDF4F8" : "#F0FDF4", color: p.catType === "Desi" ? "#7B1D4F" : "#065F46" }}>{p.catType}</span>
                          <span style={{ fontSize: 14, fontWeight: 800, color: clr, textAlign: "right", minWidth: 22 }}>{p.stock}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Recent Activity */}
              <div className="db-widget" style={{ flex: "2 1 180px", background: "#fff", borderRadius: 14, padding: "18px 14px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)", maxHeight: 340, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexShrink: 0 }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>🕐</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>Recent Activity</div>
                    <div style={{ fontSize: 10, color: "#9CA3AF" }}>Latest first</div>
                  </div>
                </div>
                {timelineLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "#9CA3AF" }}><FaSpinner style={{ animation: "spin 1s linear infinite" }} /></div>
                ) : timelineData.length === 0 ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "#9CA3AF", fontSize: 12 }}>No activity today</div>
                ) : (
                  <div style={{ overflowY: "auto", flex: 1, position: "relative" }}>
                    <div style={{ position: "absolute", left: 12, top: 0, bottom: 0, width: 2, background: "#F3F4F6" }} />
                    {timelineData.map((e, i) => {
                      const dt = e.time ? new Date(e.time) : null;
                      const timeStr = dt && !isNaN(dt.getTime())
                        ? dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
                        : "Today";
                      return (
                        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 9, position: "relative" }}>
                          <div style={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0, background: e.color + "20", border: `2px solid ${e.color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, zIndex: 1 }}>{e.icon}</div>
                          <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.text}</div>
                            {e.sub && <div style={{ fontSize: 10, color: "#9CA3AF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.sub}</div>}
                            <div style={{ fontSize: 9, color: "#9CA3AF", marginTop: 1 }}>{timeStr}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* ══════════════════════════════════════════
              OUT OF STOCK ITEMS
          ══════════════════════════════════════════ */}
          {outOfStockItems.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <SectionLabel>Out of Stock — Immediate Action Required</SectionLabel>
              <div style={{ background: "#fff", borderRadius: 14, padding: "16px 18px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)", borderTop: "3px solid #EF4444" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🚨</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#EF4444" }}>{outOfStockItems.length} Product{outOfStockItems.length > 1 ? "s" : ""} Out of Stock</div>
                    <div style={{ fontSize: 10, color: "#9CA3AF" }}>Stock = 0 • Reorder immediately</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {outOfStockItems.map((p, i) => (
                    <div key={i} style={{
                      background: "#FEF2F2", borderRadius: 8, padding: "8px 12px",
                      border: "1.5px solid #FECACA", flex: "1 1 150px", minWidth: 140,
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 3 }}>
                        <span style={{ fontSize: 10, color: "#6B7280" }}>{p.packing}</span>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 10, background: p.catType === "Desi" ? "#FDF4F8" : "#F0FDF4", color: p.catType === "Desi" ? "#7B1D4F" : "#065F46" }}>{p.catType}</span>
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 800, color: "#EF4444", marginTop: 4 }}>⚠ OUT OF STOCK</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════
              QUICK NAVIGATION
          ══════════════════════════════════════════ */}
          <div>
            <SectionLabel>Quick Navigation</SectionLabel>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>

              {/* Left panel — Expenses & Finance */}
              <div style={{ flex: "3 1 min(380px,100%)", background: "#fff", borderRadius: 14, padding: "14px 16px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>
                {(() => {
                  const groups = NAV_GROUPS
                    .filter(g => g.panel === "left")
                    .map(g => ({ g, items: g.items.filter(i => canSeeItem(g.perm, i.perm)) }))
                    .filter(({ g, items }) => canSeeSection(g.perm) && items.length > 0);
                  return groups.map(({ g, items }, gi) => (
                    <React.Fragment key={g.perm}>
                      {gi > 0 && <div style={{ borderTop: "1px solid #F3F4F6", margin: "12px 0" }} />}
                      <NavSection title={g.title} subtitle={g.subtitle} icon={g.icon} iconBg={g.iconBg} iconColor={g.iconColor} columns={3}>
                        {items.map(i => (
                          <NavItem key={i.path} label={i.label} sub={i.sub} icon={i.icon} bg={i.bg} path={i.path} navigate={navigate} />
                        ))}
                      </NavSection>
                    </React.Fragment>
                  ));
                })()}
              </div>

              {/* Right panel — Reports */}
              <div style={{ flex: "2 1 min(240px,100%)", background: "#fff", borderRadius: 14, padding: "14px 16px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>
                {(() => {
                  const groups = NAV_GROUPS
                    .filter(g => g.panel === "right")
                    .map(g => ({ g, items: g.items.filter(i => canSeeItem(g.perm, i.perm)) }))
                    .filter(({ g, items }) => canSeeSection(g.perm) && items.length > 0);
                  return groups.map(({ g, items }, gi) => (
                    <React.Fragment key={g.perm}>
                      {gi > 0 && <div style={{ borderTop: "1px solid #F3F4F6", margin: "12px 0" }} />}
                      <NavSection title={g.title} subtitle={g.subtitle} icon={g.icon} iconBg={g.iconBg} iconColor={g.iconColor} columns={2}>
                        {items.map(i => (
                          <NavItem key={i.path} label={i.label} sub={i.sub} icon={i.icon} bg={i.bg} path={i.path} navigate={navigate} />
                        ))}
                      </NavSection>
                    </React.Fragment>
                  ));
                })()}
              </div>

            </div>
          </div>

        </>
      )}

      {/* PerfTile detail modal */}
      <PerfModal tile={activeTile} todayData={T} monthlyData={M} onClose={() => setActiveTile(null)} />

      {/* Spinner keyframe + mobile styles */}
      <style>{`
        @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }

        /* ── Desktop: Quick Action groups side by side ── */
        @media (min-width: 768px) {
          .qa-groups-desktop { flex-direction: row !important; }
          .qa-grid-mobile    { display: none !important; }
        }

        /* ── Mobile dashboard: ≤ 767px ── */
        @media (max-width: 767px) {
          /* Header right row: left-align on mobile so calendar sits at left edge */
          .hd-header-right { justify-content: flex-start !important; width: 100%; }

          /* Quick Actions: hide desktop groups, show compact 2-col grid */
          .qa-groups-desktop { display: none !important; }
          .qa-grid-mobile    { display: grid !important; }
          /* Nav grid: max 2 columns on mobile */
          .nav-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .home-page-wrapper {
            padding: 14px 12px 60px !important;
          }
          .home-page-wrapper > div:first-child {
            flex-direction: column !important;
            gap: 8px !important;
            margin-bottom: 16px !important;
          }
          .home-page-wrapper > div:nth-child(3) > div {
            flex: 1 1 calc(50% - 7px) !important;
            min-width: 0 !important;
          }
          .home-page-wrapper > div:nth-child(4) > div {
            flex: 1 1 calc(50% - 6px) !important;
            min-width: 0 !important;
          }
          .home-page-wrapper > div.charts-row > div {
            flex: 1 1 100% !important;
            height: auto !important;
            min-height: 320px;
          }
          .db-widget-row {
            flex-direction: column !important;
          }
          .db-widget {
            flex: 1 1 100% !important;
            max-height: none !important;
          }
          .home-page-wrapper > div:last-child > div > div {
            flex: 1 1 100% !important;
          }
          .home-page-wrapper > div:nth-child(2) > div:last-child {
            width: 100%;
          }
          .home-page-wrapper > div:nth-child(2) > div:last-child button {
            flex: 1 1 calc(50% - 4px) !important;
            justify-content: center;
            font-size: 12px !important;
            padding: 8px 8px !important;
          }
          .hd-qa { padding: 14px 14px 16px !important; }
          /* Performance Breakdown: wrap into 2-column grid on mobile */
          .perf-breakdown-row { flex-wrap: wrap !important; overflow-x: visible !important; }
          .perf-tile { flex: 1 1 calc(50% - 5px) !important; min-width: 0 !important; }
        }

        /* ── Tablet: 768–1024px ── */
        @media (min-width: 768px) and (max-width: 1024px) {
          .home-page-wrapper {
            padding: 16px 16px 48px !important;
          }
          .db-widget {
            flex: 1 1 calc(50% - 8px) !important;
          }
        }

        /* ── Desktop perf strip: hide scrollbar ── */
        .home-page-wrapper ::-webkit-scrollbar {
          height: 0;
        }
      `}</style>
    </div>
  );
}

/* ─── quick-nav sub-components ─────────────────────────────────── */
function NavSection({ title, subtitle, icon, iconBg, iconColor, children, columns = 2 }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
        <div style={{ width: 22, height: 22, borderRadius: 6, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: iconColor, flexShrink: 0 }}>
          {icon}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: "#111827" }}>{title}</span>
          <span style={{ fontSize: 9, fontWeight: 600, color: "#9CA3AF" }}>{subtitle}</span>
        </div>
      </div>
      <div className="nav-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 6 }}>
        {children}
      </div>
    </div>
  );
}

function NavItem({ label, sub, icon, bg, path, navigate }) {
  return (
    <div
      onClick={() => navigate(path)}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "9px 10px", borderRadius: 9, cursor: "pointer",
        border: "1px solid #F3F4F6", transition: "background 0.14s, border-color 0.14s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "#FDF4F8"; e.currentTarget.style.borderColor = "#E8C5D8"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "#F3F4F6"; }}
    >
      <div style={{
        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
        background: `linear-gradient(135deg, ${bg}dd, ${bg})`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, boxShadow: `0 2px 5px ${bg}55`, color: "#fff",
      }}>
        {icon}
      </div>
      <div style={{ overflow: "hidden" }}>
        <div style={{ fontWeight: 600, fontSize: 12, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
        <div style={{ fontSize: 10, color: "#9CA3AF", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>
      </div>
    </div>
  );
}
